/**
 * ResourceFilterPanel Component
 * Multi-select checkboxes for filtering calendar events by resources
 */

import React, { useMemo, useState, useRef, useEffect } from 'react';
import {
  Checkbox,
  Radio,
  Button,
  Space,
  Badge,
  Spin,
  Divider,
  Avatar,
  Input,
  Tooltip,
} from 'antd';
import {
  UserOutlined,
  DownOutlined,
  RightOutlined,
  SearchOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useResources } from '@/hooks/useCalendars';
import type { Resource, CalendarEvent } from '@/api/types/calendars.types';
import './ResourceFilterPanel.css';

// API base URL for avatar images
const API_BASE_URL =
  import.meta.env.VITE_API_URL || 'http://api.thilo.loc:8000/v2/api';

/**
 * Get avatar URL for a resource if it's linked to a user or business partner
 * In GoldenWings: bp_id == user_id == resource_id, so instance_id works for user avatar
 */
const getResourceAvatarUrl = (
  resource: Resource,
  size: '64x64' | '255x255' = '64x64'
): string | null => {
  // Show avatars for INSTRUCTOR, PILOT, and STUDENT resources
  if (
    resource.resource_type === 'INSTRUCTOR' ||
    resource.resource_type === 'PILOT' ||
    resource.resource_type === 'STUDENT'
  ) {
    // instance_id is the BP/user ID - use it for user avatar endpoint
    if (resource.instance_id) {
      return `${API_BASE_URL}/tenants/users/id/${resource.instance_id}/avatar.jpg?resize_to=${size}`;
    }
  }
  return null;
};

/**
 * Check if resource is a person (instructor or pilot/student)
 */
const isPersonResource = (resource: Resource): boolean => {
  return (
    resource.resource_type === 'INSTRUCTOR' ||
    resource.resource_type === 'PILOT' ||
    resource.resource_type === 'STUDENT'
  );
};

/**
 * Generate tooltip content for person resources (instructors, pilots, students)
 */
const PersonTooltipContent: React.FC<{ resource: Resource }> = ({
  resource,
}) => {
  const largeAvatarUrl = getResourceAvatarUrl(resource, '255x255');

  return (
    <div className="person-tooltip-content">
      {largeAvatarUrl && (
        <Avatar
          src={largeAvatarUrl}
          size={120}
          icon={<UserOutlined />}
          className="person-tooltip-avatar"
        />
      )}
      <div className="person-tooltip-name">{resource.name}</div>
    </div>
  );
};

/** Collapsed state for each filter group */
export interface FilterGroupCollapsedState {
  AIRPORT?: boolean;
  AIRPLANE?: boolean;
  INSTRUCTOR?: boolean;
  PILOT?: boolean;
}

interface ResourceFilterPanelProps {
  /** Currently selected resource IDs */
  selectedResourceIds: string[];
  /** Callback when selection changes */
  onSelectionChange: (resourceIds: string[]) => void;
  /** Current calendar view - determines airport selection mode */
  currentView?: string;
  /** Optional: Pre-fetched resources with event counts (if provided, panel won't fetch its own) */
  airplanes?: Resource[];
  instructors?: Resource[];
  pilots?: Resource[];
  students?: Resource[];
  airports?: Resource[];
  /** Loading state when resources are provided externally */
  isLoading?: boolean;
  /** Events for cross-filtered event count calculation */
  events?: CalendarEvent[];
  /** Collapsed state for each group */
  collapsedGroups?: FilterGroupCollapsedState;
  /** Callback when collapsed state changes */
  onCollapsedGroupsChange?: (collapsed: FilterGroupCollapsedState) => void;
}

/**
 * Group resources by type for display
 */
interface ResourceGroup {
  type: 'AIRPLANE' | 'INSTRUCTOR' | 'PILOT' | 'AIRPORT';
  title: string;
  icon: string;
  resources: Resource[];
}

// Views that allow only single airport selection (vertical calendar views)
const SINGLE_AIRPORT_VIEWS = ['1day', '2day', '3day', '7day', 'week-new'];

export const ResourceFilterPanel: React.FC<ResourceFilterPanelProps> = ({
  selectedResourceIds,
  onSelectionChange,
  currentView = 'week-new',
  airplanes: externalAirplanes,
  instructors: externalInstructors,
  pilots: externalPilots,
  students: externalStudents,
  airports: externalAirports,
  isLoading: externalLoading,
  events = [],
  collapsedGroups = {},
  onCollapsedGroupsChange,
}) => {
  // Determine if we're in single airport mode
  const isSingleAirportMode = SINGLE_AIRPORT_VIEWS.includes(currentView);

  // Local state for search terms per group
  const [searchTerms, setSearchTerms] = useState<Record<string, string>>({});
  // Local state for showing search input per group
  const [searchVisible, setSearchVisible] = useState<Record<string, boolean>>(
    {}
  );
  // Refs for search inputs to auto-focus
  const searchInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Toggle collapsed state for a group
  const toggleCollapsed = (groupType: string) => {
    const newCollapsed = {
      ...collapsedGroups,
      [groupType]:
        !collapsedGroups[groupType as keyof FilterGroupCollapsedState],
    };
    onCollapsedGroupsChange?.(newCollapsed);
  };

  // Toggle search visibility
  const toggleSearch = (groupType: string) => {
    const willShow = !searchVisible[groupType];
    setSearchVisible((prev) => ({
      ...prev,
      [groupType]: willShow,
    }));
    // Clear search when hiding
    if (!willShow) {
      setSearchTerms((prev) => ({
        ...prev,
        [groupType]: '',
      }));
    }
    // Focus input when showing (with small delay to allow render)
    if (willShow) {
      setTimeout(() => {
        searchInputRefs.current[groupType]?.focus();
      }, 50);
    }
  };

  // Update search term
  const updateSearchTerm = (groupType: string, term: string) => {
    setSearchTerms((prev) => ({
      ...prev,
      [groupType]: term,
    }));
  };

  // Filter resources by search term
  const filterResourcesBySearch = (
    resources: Resource[],
    groupType: string
  ): Resource[] => {
    const term = searchTerms[groupType]?.toLowerCase() || '';
    if (!term) return resources;
    return resources.filter((r) => r.name.toLowerCase().includes(term));
  };

  // Check if resources are provided externally
  const hasExternalResources =
    externalAirplanes !== undefined ||
    externalInstructors !== undefined ||
    externalPilots !== undefined ||
    externalStudents !== undefined ||
    externalAirports !== undefined;

  // Fetch resources internally only if not provided externally
  const { data: internalAirplanes = [], isLoading: loadingAirplanes } =
    useResources(
      hasExternalResources ? undefined : { resource_type: 'AIRPLANE' }
    );
  const { data: internalInstructors = [], isLoading: loadingInstructors } =
    useResources(
      hasExternalResources ? undefined : { resource_type: 'INSTRUCTOR' }
    );
  const { data: internalPilots = [], isLoading: loadingPilots } = useResources(
    hasExternalResources ? undefined : { resource_type: 'PILOT' }
  );
  const { data: internalStudents = [], isLoading: loadingStudents } =
    useResources(
      hasExternalResources ? undefined : { resource_type: 'STUDENT' }
    );
  const { data: internalAirports = [], isLoading: loadingAirports } =
    useResources(
      hasExternalResources ? undefined : { resource_type: 'AIRPORT' }
    );

  // Use external resources if provided, otherwise use internal
  const airplanes = externalAirplanes ?? internalAirplanes;
  const instructors = externalInstructors ?? internalInstructors;
  const pilots = externalPilots ?? internalPilots;
  const students = externalStudents ?? internalStudents;
  const airports = externalAirports ?? internalAirports;

  const loading = hasExternalResources
    ? (externalLoading ?? false)
    : loadingAirplanes ||
      loadingInstructors ||
      loadingPilots ||
      loadingStudents ||
      loadingAirports;

  // Combine pilots and students for display
  const pilotsAndStudents = [...pilots, ...students];

  /**
   * Calculate cross-filtered event counts for each resource
   *
   * Logic:
   * - For each resource, count events that contain that resource
   * - BUT only count events that ALSO match all OTHER selected filters
   *
   * Example: If airplane A1 and instructor I1 are selected:
   * - For airplane A2: count events with A2 AND I1
   * - For instructor I2: count events with I2 AND A1
   * - For student S1: count events with S1 AND A1 AND I1
   */
  const crossFilteredEventCounts = useMemo(() => {
    const counts: Record<string, number> = {};

    if (!events || events.length === 0) {
      return counts;
    }

    // Group selected IDs by resource type
    const airplaneIds = new Set(airplanes.map((r) => r.id));
    const instructorIds = new Set(instructors.map((r) => r.id));
    const pilotStudentIds = new Set([...pilots, ...students].map((r) => r.id));
    const airportIds = new Set(airports.map((r) => r.id));

    const selectedAirplaneIds = selectedResourceIds.filter((id) =>
      airplaneIds.has(id)
    );
    const selectedInstructorIds = selectedResourceIds.filter((id) =>
      instructorIds.has(id)
    );
    const selectedPilotStudentIds = selectedResourceIds.filter((id) =>
      pilotStudentIds.has(id)
    );
    const selectedAirportIds = selectedResourceIds.filter((id) =>
      airportIds.has(id)
    );

    // Helper: Check if event matches a set of required resource IDs (at least one from the set)
    const eventMatchesGroup = (
      event: CalendarEvent,
      requiredIds: string[]
    ): boolean => {
      if (requiredIds.length === 0) return true; // No filter = matches all
      const eventResourceIds = event.resources.map((r) => r.resource_id);
      return requiredIds.some((id) => eventResourceIds.includes(id));
    };

    // For each resource, calculate how many events would match if we selected it
    // considering the OTHER already-selected filters
    const allResources = [
      ...airplanes,
      ...instructors,
      ...pilots,
      ...students,
      ...airports,
    ];

    for (const resource of allResources) {
      let matchingEvents = 0;

      for (const event of events) {
        const eventResourceIds = event.resources.map((r) => r.resource_id);

        // Event must contain this resource
        if (!eventResourceIds.includes(resource.id)) continue;

        // Now check if event matches OTHER selected filters (excluding this resource's group)
        let matchesOtherFilters = true;

        if (airplaneIds.has(resource.id)) {
          // This is an airplane - check instructor, pilot/student, airport filters
          matchesOtherFilters =
            eventMatchesGroup(event, selectedInstructorIds) &&
            eventMatchesGroup(event, selectedPilotStudentIds) &&
            eventMatchesGroup(event, selectedAirportIds);
        } else if (instructorIds.has(resource.id)) {
          // This is an instructor - check airplane, pilot/student, airport filters
          matchesOtherFilters =
            eventMatchesGroup(event, selectedAirplaneIds) &&
            eventMatchesGroup(event, selectedPilotStudentIds) &&
            eventMatchesGroup(event, selectedAirportIds);
        } else if (pilotStudentIds.has(resource.id)) {
          // This is a pilot/student - check airplane, instructor, airport filters
          matchesOtherFilters =
            eventMatchesGroup(event, selectedAirplaneIds) &&
            eventMatchesGroup(event, selectedInstructorIds) &&
            eventMatchesGroup(event, selectedAirportIds);
        } else if (airportIds.has(resource.id)) {
          // This is an airport - check airplane, instructor, pilot/student filters
          matchesOtherFilters =
            eventMatchesGroup(event, selectedAirplaneIds) &&
            eventMatchesGroup(event, selectedInstructorIds) &&
            eventMatchesGroup(event, selectedPilotStudentIds);
        }

        if (matchesOtherFilters) {
          matchingEvents++;
        }
      }

      counts[resource.id] = matchingEvents;
    }

    return counts;
  }, [
    events,
    airplanes,
    instructors,
    pilots,
    students,
    airports,
    selectedResourceIds,
  ]);

  // Helper to get event count for a resource (cross-filtered or from backend)
  const getEventCount = (resource: Resource): number => {
    // Prefer cross-filtered count if events are provided
    if (events && events.length > 0) {
      return crossFilteredEventCounts[resource.id] || 0;
    }
    // Fallback to backend-provided count
    return resource.event_count || 0;
  };

  // Group resources for display - ORDER: Airports, Airplanes, Instructors, Students
  const resourceGroups: ResourceGroup[] = [
    {
      type: 'AIRPORT',
      title: 'Aerodromi',
      icon: '🏢',
      resources: airports,
    },
    {
      type: 'AIRPLANE',
      title: 'Avioni',
      icon: '✈️',
      resources: airplanes,
    },
    {
      type: 'INSTRUCTOR',
      title: 'Instruktori',
      icon: '👨‍✈️',
      resources: instructors,
    },
    {
      type: 'PILOT',
      title: 'Studenti / Piloti',
      icon: '🎓',
      resources: pilotsAndStudents,
    },
  ];

  // Handle individual checkbox change
  const handleCheckboxChange = (resourceId: string, resourceType: string) => {
    // Special handling for airports in single selection mode
    if (resourceType === 'AIRPORT' && isSingleAirportMode) {
      // Get current airport IDs
      const airportIds = airports.map((a) => a.id);
      const currentAirportSelection = selectedResourceIds.filter((id) =>
        airportIds.includes(id)
      );

      // If clicking on already selected airport, do nothing (can't deselect in radio mode)
      if (currentAirportSelection.includes(resourceId)) {
        // Radio button behavior - can't deselect
        return;
      } else {
        // Replace any existing airport with the new one (radio button behavior)
        const nonAirportIds = selectedResourceIds.filter(
          (id) => !airportIds.includes(id)
        );
        onSelectionChange([...nonAirportIds, resourceId]);
      }
      return;
    }

    // Default multi-select behavior
    const newSelection = selectedResourceIds.includes(resourceId)
      ? selectedResourceIds.filter((id) => id !== resourceId)
      : [...selectedResourceIds, resourceId];
    onSelectionChange(newSelection);
  };

  // Select all in group
  const handleSelectAll = (groupResources: Resource[]) => {
    const groupIds = groupResources.map((r) => r.id);
    const otherIds = selectedResourceIds.filter((id) => !groupIds.includes(id));
    onSelectionChange([...otherIds, ...groupIds]);
  };

  // Clear all in group
  const handleClearAll = (groupResources: Resource[]) => {
    const groupIds = groupResources.map((r) => r.id);
    const newSelection = selectedResourceIds.filter(
      (id) => !groupIds.includes(id)
    );
    onSelectionChange(newSelection);
  };

  // Clear all filters
  const handleClearAllFilters = () => {
    onSelectionChange([]);
  };

  // Count selected in group
  const getSelectedCount = (groupResources: Resource[]): number => {
    const groupIds = groupResources.map((r) => r.id);
    return selectedResourceIds.filter((id) => groupIds.includes(id)).length;
  };

  if (loading) {
    return (
      <div className="resource-filter-panel">
        <div className="filter-panel-loading">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className="resource-filter-panel">
      <div className="filter-panel-header">
        <h3>Filteri</h3>
        {selectedResourceIds.length > 0 && (
          <Button
            type="link"
            size="small"
            onClick={handleClearAllFilters}
            danger
          >
            Poništi sve ({selectedResourceIds.length})
          </Button>
        )}
      </div>

      <Divider style={{ margin: '12px 0' }} />

      <div className="filter-groups">
        {resourceGroups.map((group) => {
          const selectedCount = getSelectedCount(group.resources);
          const allSelected = selectedCount === group.resources.length;
          const isCollapsed =
            collapsedGroups[group.type as keyof FilterGroupCollapsedState] ??
            false;
          const isSearchVisible = searchVisible[group.type] ?? false;
          const searchTerm = searchTerms[group.type] ?? '';
          const filteredResources = filterResourcesBySearch(
            group.resources,
            group.type
          );

          return (
            <div
              key={group.type}
              className={`filter-group ${isCollapsed ? 'collapsed' : ''}`}
            >
              <div className="group-header">
                <span
                  className="group-title clickable"
                  onClick={() => toggleCollapsed(group.type)}
                >
                  <span className="collapse-icon">
                    {isCollapsed ? <RightOutlined /> : <DownOutlined />}
                  </span>
                  <span className="group-icon">{group.icon}</span>
                  {group.title}
                  {selectedCount > 0 && (
                    <Badge
                      count={selectedCount}
                      style={{
                        backgroundColor: 'var(--color-primary)',
                        marginLeft: 8,
                      }}
                    />
                  )}
                </span>
                <Space size={4}>
                  {/* Search button */}
                  <Button
                    type="text"
                    size="small"
                    icon={
                      isSearchVisible ? <CloseOutlined /> : <SearchOutlined />
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSearch(group.type);
                    }}
                    className="group-action-btn"
                  />
                  {/* Hide "Select All" for airports in single selection mode */}
                  {!allSelected &&
                    group.resources.length > 0 &&
                    !(group.type === 'AIRPORT' && isSingleAirportMode) && (
                      <Button
                        type="link"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectAll(group.resources);
                        }}
                      >
                        Svi
                      </Button>
                    )}
                  {/* Hide "Clear" for airports in single selection mode (radio behavior) */}
                  {selectedCount > 0 &&
                    !(group.type === 'AIRPORT' && isSingleAirportMode) && (
                      <Button
                        type="link"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClearAll(group.resources);
                        }}
                      >
                        Poništi
                      </Button>
                    )}
                </Space>
              </div>

              {/* Search input */}
              {isSearchVisible && !isCollapsed && (
                <div className="group-search">
                  <Input
                    ref={(el) => {
                      if (el) searchInputRefs.current[group.type] = el.input;
                    }}
                    placeholder="Pretraži..."
                    size="small"
                    prefix={<SearchOutlined />}
                    value={searchTerm}
                    onChange={(e) =>
                      updateSearchTerm(group.type, e.target.value)
                    }
                    allowClear
                  />
                </div>
              )}

              {/* Collapsed summary - show selected names inline */}
              {isCollapsed && selectedCount > 0 && (
                <div className="collapsed-summary">
                  {group.resources
                    .filter((r) => selectedResourceIds.includes(r.id))
                    .map((r) => r.name)
                    .join(', ')}
                </div>
              )}

              {/* Expanded checkboxes */}
              {!isCollapsed && (
                <div className="group-checkboxes">
                  {filteredResources.length === 0 ? (
                    <div className="no-resources">
                      {searchTerm
                        ? 'Nema rezultata pretrage'
                        : 'Nema dostupnih resursa'}
                    </div>
                  ) : group.type === 'AIRPORT' && isSingleAirportMode ? (
                    // Radio buttons for airports in single selection mode
                    filteredResources.map((resource) => (
                      <Radio
                        key={resource.id}
                        checked={selectedResourceIds.includes(resource.id)}
                        onChange={() =>
                          handleCheckboxChange(resource.id, group.type)
                        }
                        className="resource-checkbox"
                      >
                        <span className="resource-item-content">
                          <span className="resource-name">{resource.name}</span>
                          {getEventCount(resource) > 0 && (
                            <Badge
                              count={getEventCount(resource)}
                              size="small"
                              style={{
                                backgroundColor: '#52c41a',
                                marginLeft: 6,
                              }}
                            />
                          )}
                          {!resource.is_available && (
                            <span className="resource-unavailable">
                              (nedostupan)
                            </span>
                          )}
                        </span>
                      </Radio>
                    ))
                  ) : (
                    // Checkboxes for all other resources
                    filteredResources.map((resource) => {
                      const avatarUrl = getResourceAvatarUrl(resource);
                      const isPerson = isPersonResource(resource);

                      const checkboxContent = (
                        <Checkbox
                          key={resource.id}
                          checked={selectedResourceIds.includes(resource.id)}
                          onChange={() =>
                            handleCheckboxChange(resource.id, group.type)
                          }
                          className="resource-checkbox"
                        >
                          <span className="resource-item-content">
                            {avatarUrl && (
                              <Avatar
                                src={avatarUrl}
                                size={24}
                                icon={<UserOutlined />}
                                className="resource-avatar"
                              />
                            )}
                            <span className="resource-name">
                              {resource.name}
                            </span>
                            {getEventCount(resource) > 0 && (
                              <Badge
                                count={getEventCount(resource)}
                                size="small"
                                style={{
                                  backgroundColor: '#52c41a',
                                  marginLeft: 6,
                                }}
                              />
                            )}
                            {!resource.is_available && (
                              <span className="resource-unavailable">
                                (nedostupan)
                              </span>
                            )}
                          </span>
                        </Checkbox>
                      );

                      // Wrap person resources with tooltip showing larger photo
                      if (isPerson) {
                        return (
                          <Tooltip
                            key={resource.id}
                            title={<PersonTooltipContent resource={resource} />}
                            placement="rightTop"
                            mouseEnterDelay={1}
                            color="var(--tooltip-bg, rgba(0, 0, 0, 0.85))"
                            overlayClassName="person-tooltip-overlay"
                          >
                            {checkboxContent}
                          </Tooltip>
                        );
                      }

                      return checkboxContent;
                    })
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
